import {
  type PostTaskSchedulesRequestBody,
  RecurrenceIntervalUnitEnum,
  RecurrenceWeekdayEnum,
} from '@ulysses-inc/harami_api_client'
import { format } from 'date-fns'

/**
 * 「繰り返し」の選択肢。API の頻度（intervalUnit）とは 1 対 1 ではなく、
 * 「毎日」と「N 日おき」はどちらも day で、毎日は間隔 1 に固定する（案E・2026-09-16 決定）。
 */
export type RepeatKind = 'daily' | 'ndays' | 'weekly' | 'monthly' | 'yearly'

/**
 * 予定期間の長さの選び方。1 日（日にちタイプ）か、頻度と同じ単位 × N（期間タイプ）。
 */
export type PlannedPeriodKind = 'singleDay' | 'sameUnit'

export type TaskScheduleForm = {
  name: string
  templateId: number | null
  /** 適用する現場（現場ノードの UUID）。現場グループは含まない */
  placeNodeUuids: string[]
  /** 適用開始日（YYYY-MM-DD）。繰り返しの基点でもある */
  fromDate: string
  hasThroughDate: boolean
  /** 適用終了日（YYYY-MM-DD）。hasThroughDate のときだけ使う */
  throughDate: string
  repeat: RepeatKind
  /** 間隔。毎日のときは無視して 1 を送る */
  interval: number
  /** 毎週の曜日 */
  weekdays: RecurrenceWeekdayEnum[]
  /** 毎年の月（1〜12） */
  months: number[]
  /** 毎月・毎年の日。1〜28 か、末日から数えた -1〜-3 */
  monthDays: number[]
  plannedPeriodKind: PlannedPeriodKind
  /** 期間タイプのときの個数（頻度と同じ単位） */
  plannedPeriodValue: number
  /** 開始時刻（HH:MM）。日にちタイプのときだけ使う */
  startTime: string
  /** 締切時刻（HH:MM）。日にちタイプのときだけ使う */
  dueTime: string
}

export const INTERVAL_UNIT_OF: Record<RepeatKind, RecurrenceIntervalUnitEnum> =
  {
    daily: RecurrenceIntervalUnitEnum.Day,
    ndays: RecurrenceIntervalUnitEnum.Day,
    weekly: RecurrenceIntervalUnitEnum.Week,
    monthly: RecurrenceIntervalUnitEnum.Month,
    yearly: RecurrenceIntervalUnitEnum.Year,
  }

/** 間隔の上限（HRMSHI-118 D1）。サーバも同じ値で検査する。ここでは入力欄の max に使うだけ */
export const MAX_INTERVAL: Record<RecurrenceIntervalUnitEnum, number> = {
  [RecurrenceIntervalUnitEnum.Day]: 31,
  [RecurrenceIntervalUnitEnum.Week]: 12,
  [RecurrenceIntervalUnitEnum.Month]: 12,
  [RecurrenceIntervalUnitEnum.Year]: 2,
}

export const UNIT_LABEL: Record<RecurrenceIntervalUnitEnum, string> = {
  [RecurrenceIntervalUnitEnum.Day]: '日',
  [RecurrenceIntervalUnitEnum.Week]: '週',
  [RecurrenceIntervalUnitEnum.Month]: 'か月',
  [RecurrenceIntervalUnitEnum.Year]: '年',
}

export const REPEAT_OPTIONS: { value: RepeatKind; label: string }[] = [
  { value: 'daily', label: '毎日' },
  { value: 'ndays', label: 'N 日おき' },
  { value: 'weekly', label: '毎週' },
  { value: 'monthly', label: '毎月' },
  { value: 'yearly', label: '毎年' },
]

/** 曜日（月曜始まり）。code は RRULE の BYDAY */
export const WEEKDAYS: { code: RecurrenceWeekdayEnum; label: string }[] = [
  { code: RecurrenceWeekdayEnum.Monday, label: '月' },
  { code: RecurrenceWeekdayEnum.Tuesday, label: '火' },
  { code: RecurrenceWeekdayEnum.Wednesday, label: '水' },
  { code: RecurrenceWeekdayEnum.Thursday, label: '木' },
  { code: RecurrenceWeekdayEnum.Friday, label: '金' },
  { code: RecurrenceWeekdayEnum.Saturday, label: '土' },
  { code: RecurrenceWeekdayEnum.Sunday, label: '日' },
]

/** 末日から数える日の選択肢（HRMSHI-118 D4） */
export const LAST_DAY_OPTIONS: { value: number; label: string }[] = [
  { value: -1, label: '末日' },
  { value: -2, label: '末日の 1 日前' },
  { value: -3, label: '末日の 2 日前' },
]

/** 月の頭から数える日の上限（HRMSHI-118 D3。29〜31 日は無い月があるので選べない） */
export const MAX_MONTH_DAY = 28

const weekdayCodeOf = (date: Date): RecurrenceWeekdayEnum => {
  // Date#getDay は日曜が 0
  const byGetDay: RecurrenceWeekdayEnum[] = [
    RecurrenceWeekdayEnum.Sunday,
    RecurrenceWeekdayEnum.Monday,
    RecurrenceWeekdayEnum.Tuesday,
    RecurrenceWeekdayEnum.Wednesday,
    RecurrenceWeekdayEnum.Thursday,
    RecurrenceWeekdayEnum.Friday,
    RecurrenceWeekdayEnum.Saturday,
  ]
  return byGetDay[date.getDay()] ?? RecurrenceWeekdayEnum.Monday
}

/** YYYY-MM-DD を端末の時計の暦日として読む。読めなければ null */
export const parseDateInput = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!m) return null
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatDateInput = (date: Date): string =>
  format(date, 'yyyy-MM-dd')

/**
 * 日付指定（曜日・月・日）の初期値。適用開始日から採る（その日の曜日、その月、その日。29 日以降は 28 日）。
 * 適用開始日を変えたときにも呼んで、指定を追従させる。
 */
export const dateDefaultsFor = (
  fromDate: string,
): Pick<TaskScheduleForm, 'weekdays' | 'months' | 'monthDays'> => {
  const date = parseDateInput(fromDate) ?? new Date()
  return {
    weekdays: [weekdayCodeOf(date)],
    months: [date.getMonth() + 1],
    monthDays: [Math.min(date.getDate(), MAX_MONTH_DAY)],
  }
}

export const defaultTaskScheduleForm = (today: Date): TaskScheduleForm => {
  const fromDate = formatDateInput(today)
  return {
    name: '',
    templateId: null,
    placeNodeUuids: [],
    fromDate,
    hasThroughDate: false,
    throughDate: '',
    repeat: 'daily',
    interval: 1,
    ...dateDefaultsFor(fromDate),
    plannedPeriodKind: 'singleDay',
    plannedPeriodValue: 1,
    startTime: '09:00',
    dueTime: '17:00',
  }
}

/** 予定期間が 1 日（日にちタイプ）か。毎日は常に 1 日 */
export const isSingleDayPeriod = (form: TaskScheduleForm): boolean =>
  form.repeat === 'daily' || form.plannedPeriodKind === 'singleDay'

/**
 * いま出す欄。繰り返しの選択で出入りする（案E）。
 * 不正な組み合わせは判定するのではなく、欄を出さないことで作れなくする。
 */
export type VisibleFields = {
  interval: boolean
  weekdays: boolean
  months: boolean
  monthDays: boolean
  plannedPeriod: boolean
  time: boolean
}

export const visibleFieldsOf = (form: TaskScheduleForm): VisibleFields => ({
  interval: form.repeat !== 'daily',
  weekdays: form.repeat === 'weekly',
  months: form.repeat === 'yearly',
  monthDays: form.repeat === 'monthly' || form.repeat === 'yearly',
  plannedPeriod: form.repeat !== 'daily',
  time: isSingleDayPeriod(form),
})

const sortNumbers = (values: number[]) => [...values].sort((a, b) => a - b)

const sortWeekdays = (codes: RecurrenceWeekdayEnum[]) => {
  const order = WEEKDAYS.map(w => w.code)
  return [...codes].sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/**
 * フォームの値を API の body に写す。試算（simulate）と作成で同じ形。
 * 出ていない欄の値は送らない（毎週なら曜日だけ、毎月なら日だけ、毎年なら月と日）。
 * タイムゾーンはサーバが Asia/Tokyo を埋めるので持たない。
 */
export const toRequestBody = (
  form: TaskScheduleForm,
): PostTaskSchedulesRequestBody => {
  const unit = INTERVAL_UNIT_OF[form.repeat]
  const singleDay = isSingleDayPeriod(form)
  const visible = visibleFieldsOf(form)
  return {
    name: form.name,
    templateId: form.templateId ?? 0,
    placeNodeUuids: form.placeNodeUuids,
    fromDate: form.fromDate,
    throughDate:
      form.hasThroughDate && form.throughDate !== ''
        ? form.throughDate
        : undefined,
    recurrenceRule: {
      intervalUnit: unit,
      intervalValue: form.repeat === 'daily' ? 1 : form.interval,
      byDay: visible.weekdays ? sortWeekdays(form.weekdays) : undefined,
      byMonth: visible.months ? sortNumbers(form.months) : undefined,
      byMonthDay: visible.monthDays ? sortNumbers(form.monthDays) : undefined,
      plannedPeriodUnit: singleDay ? RecurrenceIntervalUnitEnum.Day : unit,
      plannedPeriodValue: singleDay ? 1 : form.plannedPeriodValue,
      // 期間タイプは時刻を持たない（初日 0 時から最終日 24 時まで）。サーバは "00:00" 以外を弾く
      startTime: singleDay ? form.startTime : '00:00',
      dueTime: singleDay ? form.dueTime : '00:00',
    },
  }
}

/**
 * 件名・ひな形・現場が埋まっているか。試算と保存はこれが揃ってから。
 */
export const hasRequiredBasics = (form: TaskScheduleForm): boolean =>
  form.name.trim() !== '' &&
  form.templateId !== null &&
  form.placeNodeUuids.length > 0

/** フォームの判定で見つけた不正。欄名 → 理由 */
export type ClientErrors = Partial<
  Record<
    | 'fromDate'
    | 'throughDate'
    | 'weekdays'
    | 'months'
    | 'monthDays'
    | 'plannedPeriodValue',
    string
  >
>

/**
 * 欄の値だけで決まる判定。入力欄の使い勝手の一部で、規則の正はサーバ（保存の 400 と試算の errors）にある。
 * 欄同士の関係で UI の誘導（min/max・欄の出し入れ）だけでは作れてしまうものを拾う。
 * 文面はこの画面の言葉で書き、サーバの文と一致させない。
 */
export const clientErrorsOf = (
  form: TaskScheduleForm,
  today: Date,
): ClientErrors => {
  const errors: ClientErrors = {}
  const from = parseDateInput(form.fromDate)
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )
  if (from === null) {
    errors.fromDate = '適用開始日を入れてください'
  } else if (from < todayStart) {
    errors.fromDate = '適用開始日は今日以降にしてください'
  }
  if (form.hasThroughDate) {
    const through = parseDateInput(form.throughDate)
    if (through === null) {
      errors.throughDate = '適用終了日を入れてください'
    } else if (from !== null && through < from) {
      errors.throughDate = '適用終了日は適用開始日以降にしてください'
    }
  }
  const visible = visibleFieldsOf(form)
  if (visible.weekdays && form.weekdays.length === 0) {
    errors.weekdays = '曜日を 1 つ以上選んでください'
  }
  if (visible.months && form.months.length === 0) {
    errors.months = '月を 1 つ以上選んでください'
  }
  if (visible.monthDays && form.monthDays.length === 0) {
    errors.monthDays = '日を 1 つ以上選んでください'
  }
  if (visible.plannedPeriod && !isSingleDayPeriod(form)) {
    if (form.plannedPeriodValue < 1) {
      errors.plannedPeriodValue = '予定期間の長さは 1 以上にしてください'
    } else if (form.plannedPeriodValue > form.interval) {
      errors.plannedPeriodValue = `予定期間の長さは周期（${form.interval}）以下にしてください`
    }
  }
  return errors
}
